import { Component } from 'react';
import '../../../../styles/components/auth.scss';
import { ArrowLeft, Loader2, MoveRight } from 'lucide-react';
import { withRouter } from '../../../../hoc/withRouter';
import { toast } from 'react-toastify';
import { resendEmailOtp, verifyEmailOtp, verifyMobileOtp } from './services/authService';
import i18n from '../../../../i18n';

class VerifyOtp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      otp: ['', '', '', '', '', ''],
      timer: 90, // 00:90 seconds
      canResend: false,
      contactValue: '',
      contactType: '',
      maskedContact: '',
      isLoading: false,
      isResending: false,
      countryCode: '',
    };
    this.inputRefs = Array(6)
      .fill(null)
      .map(() => null);
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    const { location } = this.props;

    const data = location?.state?.data;
    const contactType = location?.state?.type;
    const countryCode = location?.state?.countryCode;
    const maskedContact = this.maskValue(data, contactType);

    this.setState({
      contactValue: data,
      contactType,
      maskedContact,
      countryCode,
    });

    // Focus first input on mount
    if (this.inputRefs[0]) {
      this.inputRefs[0].focus();
    }

    // Start countdown timer
    this.startTimer();
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  startTimer = () => {
    this.timerInterval = setInterval(() => {
      this.setState((prevState) => {
        if (prevState.timer <= 1) {
          clearInterval(this.timerInterval);
          return { timer: 0, canResend: true };
        }
        return { timer: prevState.timer - 1 };
      });
    }, 1000);
  };
  maskValue = (value, type) => {
    if (!value) return '';

    if (type === 'email') {
      const [name, domain] = value.split('@');
      const maskedName =
        name.length > 2
          ? name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
          : name[0] + '*';

      return `${maskedName}@${domain}`;
    }

    if (type === 'mobile') {
      // Show only last 3–4 digits
      const lastDigits = value.slice(-4);
      return `***${lastDigits}`;
    }

    return value;
  };

  handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...this.state.otp];
    newOtp[index] = value;
    this.setState({ otp: newOtp });

    // Auto-focus next input
    if (value && index < 5) {
      this.inputRefs[index + 1]?.focus();
    }
  };

  handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !this.state.otp[index] && index > 0) {
      this.inputRefs[index - 1]?.focus();
    }
  };

  handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const newOtp = [...this.state.otp];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) {
        newOtp[index] = char;
      }
    });

    this.setState({ otp: newOtp });

    // Focus the next empty input or last input
    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    this.inputRefs[focusIndex]?.focus();
  };

  handleResendOtp = async () => {
    if (this.state.canResend) {
      this.setState({ isResending: true });

      try {
        if (this.state.contactType === 'email') {
          const token = this.props.params.token;
          const result = await resendEmailOtp({ verification_token: token });
          const newToken = result?.response?.data?.verify_token;
          this.props.navigate(`/login/otp-verify-email/${newToken}`, { replace: true });
        } else if (this.state.contactType === 'mobile') {
          //   // Mobile OTP resend does not require token
          //   const result = await resendMobileOtp({ mobile: this.state.contactValue });
          //   toast.success(result?.response?.message || 'OTP sent successfully!');
        }
      } catch (error) {
        toast.error(error.message || 'OTP sent failed. Please try again.');
      } finally {
        this.setState({
          isResending: false,
          timer: 90,
          canResend: false,
          otp: ['', '', '', '', '', ''],
        });
        this.startTimer();
        if (this.inputRefs[0]) {
          this.inputRefs[0].focus();
        }
      }
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = this.state.otp.join('');
    const { countryCode, contactValue, contactType } = this.state;

    if (otpValue.length === 6) {
      this.setState({ isLoading: true });
      try {
        let result;
        if (contactType === 'email') {
          const token = this.props.params.token;
          result = await verifyEmailOtp({ otp: otpValue, verification_token: token });
        } else if (contactType === 'mobile') {
          result = await verifyMobileOtp({
            country_code: countryCode, // must store + code in state
            mobile: contactValue,
            otp: otpValue,
          });
        }

        const { response } = result;
        toast.success(response?.message || 'OTP Vrerification successful!');
        this.redirectUser(response?.data?.user?.role);
      } catch (error) {
        toast.error(error.message || 'OTP Vrerification failed. Please try again.');
      } finally {
        this.setState({ isLoading: false });
      }
    }
  };

  // Redirect based on user role
  redirectUser = (role) => {
    if (role === 'A') {
      window.location.href = '/dashboard';
    } else {
      window.location.href = '/';
    }
  };

  handleBack = () => {
    this.props.navigate(`/login`);
  };

  formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  render() {
    const { otp, timer, canResend, contactType, maskedContact, isLoading, isResending } =
      this.state;
    const isOtpComplete = otp.every((digit) => digit !== '');
    const contactTypeLabel = contactType === 'mobile' ? i18n.t('mobile') : i18n.t('email');

    return (
      <div className="register-container">
        <div className="login-left-section">
          <div className="auth-content">
            <div className="accent-line"></div>
            <h1 className="title">
              {i18n.t('verified_access')} <span className="highlight-l2">{i18n.t('only')}</span>
            </h1>
            <p className="subtitle-l2">{i18n.t('verify_banner_subtitle')}</p>
          </div>
        </div>

        <div className="login-right-section">
          <div className="login-card">
            <button onClick={this.handleBack} className="back-button">
              <ArrowLeft size={20} strokeWidth={1.5} />
              <span>{i18n.t('back')}</span>
            </button>

            <h3 className="welcome-title">{i18n.t('verify_otp')}</h3>
            <p className="welcome-subtitle">
              {i18n.t('verify_instruction', {
                contactType: contactTypeLabel,
                maskedContact,
              })}
            </p>

            <form onSubmit={this.handleSubmit}>
              <div className="otp-input-group">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(ref) => (this.inputRefs[index] = ref)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => this.handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => this.handleKeyDown(index, e)}
                    onPaste={index === 0 ? this.handlePaste : undefined}
                    className="otp-input"
                  />
                ))}
              </div>

              {contactType === 'email' && (
                <div className="otp-footer">
                  <span className="otp-resend-text">{i18n.t('dont_receive_the_code')}</span>
                  <button
                    type="button"
                    onClick={this.handleResendOtp}
                    disabled={!canResend || isResending}
                    className="otp-resend-button"
                  >
                    {isResending ? (
                      <>
                        <Loader2 size={16} className="spinner" />
                        <span>{i18n.t('resending')}</span>
                      </>
                    ) : (
                      <>
                        {canResend
                          ? i18n.t('resend_otp')
                          : `${i18n.t('resend_in')} ${this.formatTimer(timer)}`}
                      </>
                    )}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={!isOtpComplete || isLoading}
                className="button1 button1-gradient"
              >
                {i18n.t('verify_and_continue')}
                {isLoading ? <Loader2 className="animate-spin" /> : <MoveRight strokeWidth={1.5} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(VerifyOtp);
